import { useMemo, useState } from "react"
import { useSearch } from "@tanstack/react-router"

import { Avatar, Button, ProductlaneIcon } from "@productlane/ui"

import { toast } from "@/components/toast"
import { api } from "@/lib/api"
import { NotFound } from "@/screens/not-found"

function UnsubscribeContent({ token }: { token: string }) {
	const [isUnsubscribed, setIsUnsubscribed] = useState(false)

	const getWorkspaceByTokenMutation =
		api.user.getWorkspaceByUnsubscribeToken.useQuery({
			token,
		})

	const unsubscribeMutation = api.user.unsubscribeFromEmails.useMutation({
		onSuccess: () => {
			setIsUnsubscribed(true)
		},
		onError: (error) => {
			toast.error({
				title: "Something went wrong",
				description: error.message,
			})
		},
	})

	const data = useMemo(() => {
		if (!getWorkspaceByTokenMutation.data) return null
		return getWorkspaceByTokenMutation.data
	}, [getWorkspaceByTokenMutation.data])

	if (!data && getWorkspaceByTokenMutation.isLoading) return null
	if (!data && !getWorkspaceByTokenMutation.isLoading) return <NotFound />

	const handleUnsubscribe = () => {
		if (!token) {
			toast.error({
				title: "Something went wrong",
				description: "No unsubscribe token provided.",
			})
			return
		}
		unsubscribeMutation.mutate({ token })
	}

	if (isUnsubscribed) {
		return (
			<div className="mx-auto max-w-md text-start">
				<div className="rounded-lg p-6">
					{data?.workspace?.logoUrl ? (
						<Avatar
							name="Workspace logo"
							className="h-12 w-12"
							roundness="md"
							image={data?.workspace.logoUrl}
						/>
					) : (
						<ProductlaneIcon className="h-12 w-12" fill="#16BDCA" />
					)}
					<h1 className="mt-6 text-2xl font-bold text-primary">
						Successfully unsubscribed
					</h1>
					<p className="mt-2 text-secondary">
						{data?.contact &&
							`You have been unsubscribed from new product update emails from ${data.workspace?.name}.`}
						{data?.user &&
							`You have been unsubscribed from Productlane email notifications.`}
					</p>
				</div>
			</div>
		)
	}

	if (!token) {
		return (
			<div className="mx-auto max-w-md text-start">
				<div className="rounded-lg p-6">
					<h1 className="mb-4 text-2xl font-bold text-primary">
						Invalid Unsubscribe Link
					</h1>
					<p className="text-secondary">
						This unsubscribe link is invalid or has expired. Please contact
						support if you need assistance.
					</p>
				</div>
			</div>
		)
	}

	if (data?.contact) {
		return (
			<div className="mx-auto flex max-w-md flex-col items-start justify-center text-start">
				{data?.workspace?.logoUrl ? (
					<Avatar
						name="Workspace logo"
						className="h-12 w-12"
						roundness="md"
						image={data?.workspace.logoUrl}
					/>
				) : (
					<ProductlaneIcon className="h-12 w-12" fill="#16BDCA" />
				)}
				<h1 className="mt-6 text-3xl font-bold tracking-tight text-gray-900 sm:text-3xl dark:text-gray-50">
					Unsubscribe from Product Updates
				</h1>
				<p className="mb-6 mt-2 text-secondary">
					Click the button below to stop receiving new product update emails
					from {data?.workspace?.name}.
				</p>

				<Button
					onClick={handleUnsubscribe}
					disabled={unsubscribeMutation.isLoading}
					variant="default"
					size="lg"
				>
					{unsubscribeMutation.isLoading ? "Unsubscribing..." : "Unsubscribe"}
				</Button>
			</div>
		)
	}

	if (data?.user) {
		return (
			<div className="mx-auto flex max-w-md flex-col items-start justify-center text-start">
				<ProductlaneIcon className="h-12 w-12" fill="#16BDCA" />
				<h1 className="mt-6 text-3xl font-bold tracking-tight text-gray-900 sm:text-3xl dark:text-gray-50">
					Unsubscribe from emails
				</h1>
				<p className="mb-6 mt-2 text-secondary">
					Click the button below to unsubscribe from all Productlane email
					notifications, including new threads, replies, mentions, and other.
				</p>

				<Button
					onClick={handleUnsubscribe}
					disabled={unsubscribeMutation.isLoading}
					variant="default"
					size="lg"
				>
					{unsubscribeMutation.isLoading ? "Unsubscribing..." : "Unsubscribe"}
				</Button>
			</div>
		)
	}
}

export const UnsubscribeScreen = () => {
	const token = useSearch({ strict: false, select: (s) => s.token })

	if (!token) {
		return <NotFound />
	}

	return (
		<div className="flex flex-col gap-8 px-4 py-16 md:h-dvh md:flex-row md:items-center md:justify-center">
			<UnsubscribeContent token={token} />
			{token && (
				<>
					<p className="absolute bottom-4 hidden w-full items-center justify-center text-center text-sm text-tertiary md:flex">
						Manage your notification preferences{" "}
						<a
							href="https://productlane.com/settings/notifications"
							target="_blank"
							rel="noopener noreferrer"
							className="ml-1 text-green-500 underline hover:text-green-600 dark:text-green-500 dark:hover:text-green-400"
						>
							here
						</a>
						.
					</p>
					<p className="bottom-4 flex w-full items-center justify-center text-center text-sm text-tertiary md:hidden">
						Manage your notification preferences{" "}
						<a
							href="https://productlane.com/settings/notifications"
							target="_blank"
							rel="noopener noreferrer"
							className="ml-1 text-green-500 underline hover:text-green-600 dark:text-green-500 dark:hover:text-green-400"
						>
							here
						</a>
						.
					</p>
				</>
			)}
		</div>
	)
}
